/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IEveQuickOrderData } from './eve-quick-order.data';
import { IEveQuickOrderProps } from './eve-quick-order.props.autogenerated';
import { EveQuickOrderRow, IEveQuickOrderRowResources } from './components/EveQuickOrderRow';
import { EveQuickOrderButtonRow, IEveQuickOrderButtonRowResources } from './components/EveQuickOrderButtonRow';
import { debounce } from 'lodash';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { AsyncResult, CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { EveQuickOrderHeader, IEveQuickOrderHeaderResources } from './components/EveQuickOrderHeader';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import { IOrderUploadItem } from '../order-upload/order-upload';
import {
    getAdvancedNotesAsync,
    getInventoryLevelRangesAsync,
    getProductsByManufacturerItemIdAsync
} from '../../actions/DataActionExtension.g';
import {
    IAdvancedNoteEntity,
    IManufacturerCodeResponseEntity,
    IProfileDataMap,
    ISearchTextProductMap
} from '../../actions/DataServiceEntities.g';
import { CommercePropertyValueEnum } from '../allowable-freight/utils/CommercePropertyValueEnum';
import GetProductEligibilityAction, { GetProductEligibilityInput } from '../../actions/get-product-eligibility.action';
import { SupersessionKey } from '../../themes/gardner-theme/models/SupersessionKey';
export interface IEveQuickOrderViewProps extends IEveQuickOrderProps<IEveQuickOrderData> {
    quickOrderTable: React.ReactElement;
    quickOrderHeader: React.ReactElement;
    quickOrderWrapper: IModuleProps;
    handleUploadedItems: (items: IOrderUploadItem[]) => void;
    orderUploadWrapper: INodeProps;
    guideText: React.ReactElement | undefined;
}

export interface IEveQuickOrderRowData {
    id: number;
    value: string;
    quantity: string;
    product: SimpleProductExt | null;
    availableProducts: SimpleProductExt[];
    rowError: RowError;
    manufacturerCode: string;
    advancedNotes: IAdvancedNoteEntity[];
}

interface IEveQuickOrderState {
    rows: IEveQuickOrderRowData[];
    // @ts-expect-error
    debounce: debounce;
    loading: boolean;
}

export enum RowError {
    ProductNotFound,
    InvalidQuantity,
    MultipleChoicesAvailable,
    NotEligbile,
    None,
    Unavailable
}

export interface ISearchTextProductMapExt extends ISearchTextProductMap {
    SearchText?: string;
    Products?: SimpleProductExt[];
}

export interface SimpleProductExt extends SimpleProduct {
    IsEligible?: boolean;
    IsUnavailable?: boolean;
}

interface EligibleItem {
    ItemId: string;
    IsEligible: boolean;
    IsAssorted?: boolean;
}

export interface EligibleItems extends Array<EligibleItem> {}

/**
 * Enables users to quickly order products using item ids
 * EveQuickOrder component
 * @extends {React.PureComponent<IEveQuickOrderProps<IEveQuickOrderData>>}
 */
class EveQuickOrder extends React.PureComponent<IEveQuickOrderProps<IEveQuickOrderData>, IEveQuickOrderState> {
    private _defaultMinQueryLength = 4;
    private _manufacturerCodeExtensionPropertyKey = 'ManufacturerCode';
    constructor(props: IEveQuickOrderProps<IEveQuickOrderData>) {
        super(props);

        this._handleRowDelete = this._handleRowDelete.bind(this);
        this._generateRow = this._generateRow.bind(this);
        this._renderTable = this._renderTable.bind(this);
        this._updateRowValue = this._updateRowValue.bind(this);
        this._addTwoRows = this._addTwoRows.bind(this);
        this._handleUploadedItems = this._handleUploadedItems.bind(this);
        this._tableInit = this._tableInit.bind(this);
        this._handleAddToCart = this._handleAddToCart.bind(this);
        this._handleSelectedProductChange = this._handleSelectedProductChange.bind(this);
        this._reorderRows = this._reorderRows.bind(this);

        this.state = {
            rows: [],
            debounce: this._initDebounceFn(),
            loading: false
        };
    }

    public componentDidMount() {
        this._tableInit();
    }

    /**
     * Initialises the table to the module configuration
     * @private
     */
    private _tableInit() {
        const {
            config: { defaultRows }
        } = this.props;
        const rows: IEveQuickOrderRowData[] = [];
        for (let i = 0; i < defaultRows; i++) {
            rows.push(this._generateEmptyRow(i + 1));
        }
        this.setState({ rows });
    }

    /**
     * Initializes the debounce function for all input forms
     * @private
     */
    private _initDebounceFn() {
        return debounce(async (data: { searchText: string[]; id: number }) => {
            const responseData = await this.getProductSearchResult(data.searchText);
            if (ArrayExtensions.hasElements(responseData) && ArrayExtensions.hasElements(responseData[0].Products)) {
                const notes = await getAdvancedNotesAsync({ callerContext: this.props.context.actionContext }, [
                    responseData[0].Products[0]?.ItemId!
                ]);
                this._updateRowProduct(data.id, responseData[0].Products, notes || [], data.searchText[0]);
            } else {
                const rows = [...this.state.rows];
                const idx = rows.findIndex(r => r.id === data.id);
                rows[idx].rowError = this._updateRowErrorState(rows[idx]);
                this.setState({ rows });
            }
        }, 500);
    }

    /**
     * Adds all valid items to cart
     * @private
     */
    private async _handleAddToCart() {
        const products = this.state.rows.filter(row => !!row.product && row.rowError === RowError.None);
        // 6321 - Resolve issue when the same item is added to the quick order form more than once
        const dummyProducts = [...products];
        const combinedProducts = dummyProducts.reduce(
            (acc: IEveQuickOrderRowData[], cur: IEveQuickOrderRowData): IEveQuickOrderRowData[] => {
                const row = acc.length > 0 && acc.find(({ product }) => product?.ItemId === cur.product?.ItemId);
                if (row) {
                    row.quantity = (parseInt(row.quantity, 10) + parseInt(cur.quantity, 10)).toString();
                } else {
                    acc.push(cur);
                }
                return acc;
            },
            []
        );

        if (combinedProducts.length) {
            const cartState = this.props.data.cart.result;
            const input = combinedProducts.map(product => {
                return {
                    product: product.product!,
                    count: parseInt(product.quantity, 10),
                    catalogId: 0
                };
            });
            if (cartState) {
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0 });
                }
                this.setState({ loading: true });
                await cartState.addProductsToCart(input);
                this.setState({ loading: false });
                this._tableInit();
            }
        }
    }

    /**
     * Gets product by item id
     * @param data item ids
     * @private
     */
    private async getProductSearchResult(data: string[]) {
        const itemIds = data.map(d => d.toString());

        const {
            context: { actionContext }
        } = this.props;
        const response: ISearchTextProductMapExt[] = await getProductsByManufacturerItemIdAsync(
            { callerContext: actionContext, queryResultSettings: { Paging: { Top: 9999 } } },
            itemIds,
            ''
        );

        // Get products eligbility
        const list: EligibleItems = [];
        let inventoryRanges: AsyncResult<IProfileDataMap[]> | undefined;
        if (itemIds.length) {
            const cart = this.props.data.cart;

            const newItemIds: string[] = [];

            // get Superseded products
            response.forEach(r => {
                r.Products?.forEach(p => {
                    if (p.ItemId) {
                        newItemIds.push(p.ItemId);
                    }
                });
            });
            const input = new GetProductEligibilityInput(newItemIds, false, cart?.result?.cart?.Id || '', null, null, null);
            const eligibilityData = GetProductEligibilityAction(input, this.props.context.actionContext);
            inventoryRanges = getInventoryLevelRangesAsync({ callerContext: this.props.context.actionContext }, newItemIds);
            const products = response[0].Products;
            await Promise.all([eligibilityData, inventoryRanges]);

            if (eligibilityData?.result?.length && products) {
                eligibilityData?.result?.forEach((i: any) => {
                    const id = this._getExtensionPropertyValueByKey(i.ExtensionProperties, 'ItemId', CommercePropertyValueEnum.string);
                    list.push({
                        ItemId: String(id),
                        IsEligible: i.IsEligible,
                        IsAssorted: i.IsAssorted
                    });
                });
            }
        }
        response.forEach(pr =>
            pr.Products?.forEach(p => {
                const eligibilityRecord = list.find(l => l.ItemId === p.ItemId);
                if (eligibilityRecord) {
                    p.IsEligible = eligibilityRecord.IsEligible;
                    p.IsUnavailable = !eligibilityRecord.IsAssorted;
                } else {
                    p.IsEligible = false;
                }
                const currentInvString = this.getProductSupersessionProperty(p, SupersessionKey.Inventory) || '0';
                const currentInventory = parseInt(currentInvString, 10);
                inventoryRanges?.result?.map(ir => {
                    const tempInvRange = ir.Profile?.find(invP => invP.ItemId === p.ItemId && invP.Code === 'OOS');
                    if (tempInvRange) {
                        const OosUpperBound =
                            tempInvRange.hasOwnProperty('UpperBoundInclusive') && tempInvRange.UpperBoundInclusive !== undefined
                                ? tempInvRange.UpperBoundInclusive
                                : 0;
                        p.IsUnavailable = currentInventory <= OosUpperBound;
                    }
                });
            })
        );
        // response.forEach((p: any) => {
        //     p.Products.forEach((pr: SimpleProductExt) => {
        //         list.forEach(i => {
        //             if (pr?.ItemId === i.ItemId) {
        //                 pr.IsEligible = i.IsEligible;
        //             }
        //         });
        //     });
        // });

        return response;
    }

    private getProductSupersessionProperty = (product: SimpleProductExt, extensionKey: SupersessionKey) => {
        const properties = product.ExtensionProperties;
        if (properties) {
            return properties.find(pr => pr.Key === extensionKey)?.Value?.StringValue || null;
        }
        return null;
    };

    /**
     * Gets manufacturer codes from data
     * @private
     */
    private _getManufacturerCodeList = (row: IEveQuickOrderRowData): IManufacturerCodeResponseEntity[] => {
        const codesResults = this.props.data.manufacturerCodes.result ? this.props.data.manufacturerCodes.result : [];

        //@ts-ignore
        const manufacturerCodes: IManufacturerCodeResponseEntity[] = codesResults.manufacturerCodes;

        if (row.availableProducts.length) {
            const availableCodes = row.availableProducts.map(p => this._getManufacturerCodeForRow([p]));
            return manufacturerCodes.filter(code => availableCodes.includes(code.GDNManufacturerCode || ''));
        }

        return manufacturerCodes;
    };

    /**
     * Renders the header of the page
     * @private
     */
    private _renderHeading() {
        const {
            resources: { addAllToCartText, clearFormText },
            config: { helpText, title }
        } = this.props;
        const resources: IEveQuickOrderHeaderResources = {
            addAllToCartText: addAllToCartText,
            clearAllText: clearFormText
        };
        return (
            <EveQuickOrderHeader
                heading={title}
                helpText={helpText || ''}
                resources={resources}
                canAddToCart={this._getAllAddedProducts().length}
                handleClearAll={this._tableInit}
                handleAddToCart={this._handleAddToCart}
            />
        );
    }

    /**
     * Retrieves the value of an extension property using the given key
     * @private
     * @param {(CommerceProperty[] | undefined)} properties
     * @param {string} key
     * @param {CommercePropertyValueEnum} valueType
     * @memberof EveQuickOrder
     * @return the extension property value associated with the given key
     */
    private _getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    /**
     * Renders the add to bag and reset form button row
     * @private
     */
    private _renderButtonRow() {
        const {
            resources: { addAllToCartText, clearFormText, addMoreRowsText }
        } = this.props;
        const resources: IEveQuickOrderButtonRowResources = {
            addAllToCartText: addAllToCartText,
            clearAllText: clearFormText,
            addMoreRowsText: addMoreRowsText
        };
        const { courseInCartText, invoiceInCartText } = this.props.resources;
        let courseId: string | number | boolean | null | undefined;
        if (this.props.data.cart.result?.hasInvoiceLine) {
            return (
                <div className='eve-button-row--error'>
                    <div className='eduerrors'>{invoiceInCartText}</div>
                </div>
            );
        }
        const cart = this.props.data.cart.result?.cart;
        const cartlines = cart?.CartLines;
        if (cartlines && cartlines.length) {
            courseId = this._getExtensionPropertyValueByKey(cartlines[0].ExtensionProperties, 'CourseId', CommercePropertyValueEnum.string);
        }

        if (courseId) {
            return (
                <div className='eve-button-row--error'>
                    <div className='eduerrors'>{courseInCartText}</div>
                </div>
            );
        }

        return (
            <EveQuickOrderButtonRow
                resources={resources}
                canAddToCart={this._getAllAddedProducts().length}
                handleClearAll={this._tableInit}
                handleAddToCart={this._handleAddToCart}
                isLoading={this.state.loading}
            />
        );
    }

    /**
     * gets all products
     * @private
     */
    private _getAllAddedProducts() {
        return this.state.rows.map(row => row.product?.RecordId).filter(pr => !!pr);
    }

    /**
     * Generates a row component using row data
     * @param row row data to generate from
     * @param isLast whether its the last row in the form
     * @private
     */
    private _generateRow(row: IEveQuickOrderRowData, isLast: boolean) {
        const resources: IEveQuickOrderRowResources = {
            defaultManufacturerCodeText: this.props.resources.defaultDropdownText,
            deleteText: this.props.resources.deleteHeading,
            productEligibilityText: this.props.resources.productEligibilityText,
            supersededProductText: this.props.resources.supersededProductText
        };
        return (
            <EveQuickOrderRow
                id={row.id}
                onDelete={this._handleRowDelete}
                onRowChange={this._updateRowValue}
                onAddRows={this._addTwoRows}
                value={row.value}
                quantity={row.quantity}
                product={row.product}
                availableProducts={row.availableProducts}
                currencyFormatter={this.props.context.cultureFormatter.formatCurrency}
                isLastRow={isLast}
                rowError={row.rowError}
                onSelectedProductChange={this._handleSelectedProductChange}
                manufacturerCodes={this._getManufacturerCodeList(row)}
                resources={resources}
                manufacturerCode={row.manufacturerCode}
                advancedNotes={row.advancedNotes}
            />
        );
    }

    /**
     * Handles the product in a row changing
     *
     * @private
     * @param {number} id to change
     * @param {number} productId product which has been selected
     * @memberof EveQuickOrder
     */
    private _handleSelectedProductChange(id: number, productId: number) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(r => r.id === id);
        rows[idx].product = rows[idx].availableProducts.find(pr => pr.RecordId === productId) || rows[idx].product;
        this.setState({ rows });
    }

    /**
     * Handles data from the upload module
     * @param items items uploaded
     * @private
     */
    private async _handleUploadedItems(items: IOrderUploadItem[]) {
        const itemids = items.map(it => `${it.manufacturer}${it.part_number}`);
        // 6251 - Resolve an intermittent issue when upload files are extremely large.
        const chunkSize = 50;
        const chunks = [];
        for (let i = 0; i < itemids.length; i += chunkSize) {
            const chunk = itemids.slice(i, i + chunkSize);
            chunks.push(chunk);
        }
        const promises = chunks.map(async chunk => {
            return this.getProductSearchResult(chunk);
        });
        const promiseResult = await Promise.all(promises);
        const response: ISearchTextProductMapExt[] = [];
        promiseResult.map(res => response.push(...res));
        // const response = await this.getProductSearchResult(itemids);
        const advancedNotes = await getAdvancedNotesAsync({ callerContext: this.props.context.actionContext }, itemids);
        let rows = [...this.state.rows];
        const idx = rows.findIndex(r => !r.value);
        rows = rows.slice(0, idx);
        let rowId = rows.length ? rows[rows.length - 1].id : 1;
        // response.forEach((it, index) => {
        //     const uploadItem = items.find(item => {
        //         const partNum = item.part_number.toString().toLowerCase();
        //         const partNumWithMfr = `${item.manufacturer?.toString().toLowerCase()}${item.part_number?.toString().toLowerCase()}`;
        //         const searchText = it.SearchText?.toLowerCase();
        //         return partNum === searchText || partNumWithMfr === searchText;
        //     });
        //     const foundProduct = ArrayExtensions.hasElements(it.Products);
        //     const products = it.Products || [];
        //     // const eligibleProducts = products.filter(pr => pr.IsEligible);
        //     // const filteredProducts = products.length > 0 && eligibleProducts.length > 0 ? eligibleProducts : products;
        //     const manufacturerCode = this._getManufacturerCodeForRow(products);
        //     // const manufacturerCode = this._getManufacturerCodeForRow(filteredProducts);
        //     const searchText = it.SearchText || '';
        //     const qty = uploadItem?.qty || 1;
        //     rows.push(
        //         this._generateRowData(
        //             products,
        //             // filteredProducts,
        //             rowId++,
        //             manufacturerCode,
        //             searchText,
        //             advancedNotes || [],
        //             foundProduct,
        //             qty
        //             // items[idx].qty
        //         )
        //     );
        // });

        // 6321 - Change to iterate through the item data from the form vs the response data to resolve an issue retrieving the correct qty when the same item is added more than once
        items.forEach((it, index) => {
            const responseMatch = response.find(r => {
                const partNum = it.part_number.toString().toLowerCase();
                const partNumWithMfr = `${it.manufacturer?.toString().toLowerCase()}${it.part_number?.toString().toLowerCase()}`;
                const searchText = r.SearchText?.toLowerCase();
                return partNum === searchText || partNumWithMfr === searchText;
            });
            const foundProduct = ArrayExtensions.hasElements(responseMatch?.Products);
            const products = responseMatch?.Products || [];
            const manufacturerCode = this._getManufacturerCodeForRow(products);
            const searchText = responseMatch?.SearchText || '';
            const qty = it.qty || 1;
            rows.push(
                this._generateRowData(
                    products,
                    rowId++,
                    manufacturerCode,
                    searchText,
                    advancedNotes || [],
                    foundProduct,
                    qty
                    // items[idx].qty
                )
            );
        });
        while (rows.length < this.props.config.defaultRows) {
            rows.push({
                ...this._generateEmptyRow(rowId++)
            });
        }

        this.setState({ rows });
    }

    private _generateRowData(
        products: SimpleProductExt[],
        rowId: number,
        manufacturerCode: string,
        searchText: string,
        advancedNotes: IAdvancedNoteEntity[],
        foundProduct: boolean,
        quantity: number
    ) {
        let rowError = RowError.None;
        if (!foundProduct) {
            rowError = RowError.ProductNotFound;
        } else if (products.length > 1 && manufacturerCode === '') {
            rowError = RowError.MultipleChoicesAvailable;
        } else if (quantity < 1) {
            rowError = RowError.InvalidQuantity;
        } else if (!products[0].IsEligible) {
            rowError = RowError.NotEligbile;
        } else if (products[0].IsUnavailable) {
            rowError = RowError.Unavailable;
        }

        const row: IEveQuickOrderRowData = {
            ...this._generateEmptyRow(rowId),
            product: foundProduct ? products[0] : null,
            availableProducts: products || [],
            value: this._stripManufacturerFromSearchText(manufacturerCode, searchText),
            quantity: quantity.toString(),
            rowError: rowError,
            advancedNotes: advancedNotes || [],
            manufacturerCode: manufacturerCode
        };

        return row;
    }

    private _stripManufacturerFromSearchText(manufacturerCode: string, searchText: string) {
        if (manufacturerCode === '') {
            return searchText;
        }
        // eslint-disable-next-line security/detect-non-literal-regexp
        return searchText.replace(new RegExp(manufacturerCode, 'i'), '');
    }

    private _getManufacturerCodeForRow(products: SimpleProductExt[]): string {
        // 6273 - Auto select KOH when products returned are only MTD or KOH manufacturers
        const kohConstant = 'KOH';
        const mtdConstant = 'MTD';
        const prod = products && products[0];
        if (prod && products?.length === 1) {
            const mfrCodeVal = this._getExtensionPropertyValueByKey(
                prod.ExtensionProperties,
                this._manufacturerCodeExtensionPropertyKey,
                CommercePropertyValueEnum.string
            );
            return (mfrCodeVal as string) || '';
        } else if (products && products.length > 1) {
            // 6273 - Auto select KOH when products returned are only MTD or KOH manufacturers
            const onlyKohOrMtd = products.every(
                p =>
                    mtdConstant ===
                        this._getExtensionPropertyValueByKey(
                            p.ExtensionProperties,
                            this._manufacturerCodeExtensionPropertyKey,
                            CommercePropertyValueEnum.string
                        ) ||
                    kohConstant ===
                        this._getExtensionPropertyValueByKey(
                            p.ExtensionProperties,
                            this._manufacturerCodeExtensionPropertyKey,
                            CommercePropertyValueEnum.string
                        )
            );
            return onlyKohOrMtd ? kohConstant : '';
        }
        return '';
    }

    /**
     * Renders the quick order table
     * @private
     */
    private _renderTable() {
        const {
            resources: {
                manufacturerCodeTableHeading,
                partNumberTableHeading,
                quantityTableHeading,
                itemNameHeading,
                priceHeading,
                deleteHeading,
                availabilityHeading
            }
        } = this.props;
        const rows = [
            manufacturerCodeTableHeading,
            partNumberTableHeading,
            quantityTableHeading,
            itemNameHeading,
            priceHeading,
            availabilityHeading,
            deleteHeading
        ];
        return (
            <div>
                <ul className={'q-table'}>
                    <li className='q-table-header'>
                        {rows.map((row, i) => (
                            <div key={row} className={`q-col q-col-${i + 1}`}>
                                {row}
                            </div>
                        ))}
                    </li>
                    {this._reorderRows().map((r, i) => (
                        <li className={`q-table-row ${r.rowError !== RowError.None && 'q-table-row--error'}`} key={r.id}>
                            {this._generateRow(r, i === this.state.rows.length - 1)}
                        </li>
                    ))}
                    <li className='q-table-row'>{this._renderButtonRow()}</li>
                </ul>
            </div>
        );
    }

    private _reorderRows() {
        const rows = [...this.state.rows];
        const errorRows = rows.filter(row => row.rowError !== RowError.None);
        const nonErrorRows = rows.filter(row => row.rowError === RowError.None);
        return [...errorRows, ...nonErrorRows];
    }

    /**
     * Handles removing a row
     * @param id id to remove
     * @private
     */
    private _handleRowDelete(id: number) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(r => r.id === id);
        if (idx > -1) {
            rows.splice(idx, 1);
            const newRowId = rows[rows.length - 1].id + 1;
            if (rows.length < this.props.config.defaultRows) {
                rows.push(this._generateEmptyRow(newRowId));
            }
            this.setState({ rows });
        }
    }

    /**
     * Generates an empty row
     * @param id id to initialize row with
     * @private
     */
    private _generateEmptyRow(id: number) {
        return {
            id,
            value: '',
            quantity: '1',
            product: null,
            availableProducts: [],
            rowError: RowError.None,
            manufacturerCode: '',
            advancedNotes: []
        };
    }

    /**
     * Updates the product in a row
     * @param id id of row to update
     * @param products product to add to row
     * @private
     */
    private _updateRowProduct(id: number, products: SimpleProductExt[], notes: IAdvancedNoteEntity[], searchText: string) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(row => row.id === id);
        const manufacturerCode = this._getManufacturerCodeForRow(products);
        rows[idx] = this._generateRowData(products || [], rows[idx].id, manufacturerCode, searchText, notes, true, 1);

        this.setState({ rows });
    }

    private _updateRowErrorState(row: IEveQuickOrderRowData): RowError {
        const {
            config: { maxQuantity }
        } = this.props;
        const qty = parseInt(row.quantity, 10);
        let rowError: RowError = RowError.None;
        if (row.value && !row.product) {
            rowError = RowError.ProductNotFound;
        } else if (qty === 0 || Number.isNaN(qty) || qty < 1 || qty > (maxQuantity || 1000)) {
            rowError = RowError.InvalidQuantity;
        } else if (row.product?.IsEligible === false) {
            rowError = RowError.NotEligbile;
        }
        return rowError;
    }

    /**
     * Updates value or quantity of row
     * @param id id of row to update
     * @param value value to update
     * @param quantity quantity to update
     * @param manufacturerCode Selected manufacturer code
     * @private
     */
    private _updateRowValue(id: number, value: string, quantity: string, manufacturerCode: string) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(row => row.id === id);
        if (rows[idx].value !== value) {
            this._getProductByRowQuery(id, value, manufacturerCode);
        }
        if (rows[idx].quantity !== quantity) {
            rows[idx].quantity = quantity;
            rows[idx].rowError = this._updateRowErrorState(rows[idx]);
        }
        if (rows[idx].manufacturerCode !== manufacturerCode) {
            rows[idx].manufacturerCode = manufacturerCode;
            rows[idx].rowError = this._updateRowErrorState(rows[idx]);
            if (rows[idx].availableProducts.length > 1) {
                const foundProduct = this._getProductManufacturerById(manufacturerCode, rows[idx].availableProducts);
                rows[idx].product = foundProduct;
                rows[idx].rowError = this._updateRowErrorState(rows[idx]);
            } else if (rows[idx].value !== '') {
                this._getProductByRowQuery(id, value, manufacturerCode);
            }
        }
        rows[idx].value = value;
        rows[idx].quantity = quantity;
        rows[idx].manufacturerCode = manufacturerCode;
        this.setState({ rows });
    }

    private _getProductManufacturerById(mfrCode: string, products: SimpleProductExt[]) {
        let _product = products[0];
        products.forEach(pr => {
            if (pr.ItemId!.includes(mfrCode)) {
                _product = pr;
            }
        });
        return _product;
    }

    /**
     * Calls the api when a value is updated
     * @param id id of row to update
     * @param searchText text to search for
     * @param manufacturerCode
     * @private
     */
    private _getProductByRowQuery(id: number, searchText: string, manufacturerCode: string) {
        // 6297 - Made the searchText length check that it is greater than or equal to the minimum query length so that it makes more sense to the user in site builder.
        if (searchText.length > (this.props.config.minQueryLength || this._defaultMinQueryLength)) {
            let textToSearch = searchText;
            if (manufacturerCode) {
                textToSearch = manufacturerCode + textToSearch;
            }
            this.state.debounce({ searchText: [textToSearch], id });
        }
    }

    /**
     * Adds two blank rows
     * @private
     */
    private _addTwoRows() {
        const rows = [...this.state.rows];
        const newRowId = rows[rows.length - 1].id + 1;
        rows.push(this._generateEmptyRow(newRowId));
        rows.push(this._generateEmptyRow(newRowId + 1));
        this.setState({ rows });
    }

    /**
     * To handle paragraph change.
     * @param event - To handle text change event.
     * @name - HandleParagraphChange
     * @public
     * @returns - The Paragraph value.
     */
    public handleGuideTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.orderUploadGuideText = event.target.value);

    public render(): JSX.Element | null {
        const { orderUploadGuideText } = this.props.config;
        const guideText = orderUploadGuideText && (
            <Msdyn365.RichTextComponent
                text={orderUploadGuideText}
                className='eve-quick-order-text-wrapper--text'
                editProps={{ onEdit: this.handleGuideTextChange, requestContext: this.props.context.request }}
            />
        );

        const viewProps: IEveQuickOrderViewProps = {
            ...this.props,
            handleUploadedItems: this._handleUploadedItems,
            quickOrderTable: this._renderTable(),
            quickOrderHeader: this._renderHeading(),
            quickOrderWrapper: {
                className: classNames('eve-quick-order'),
                tag: 'div',
                moduleProps: this.props
            },
            orderUploadWrapper: {
                tag: 'div',
                className: 'qo-order-upload-wrapper'
            },
            guideText: guideText
        };
        return this.props.renderView(viewProps);
    }
}

export default EveQuickOrder;
